import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

// Use dynamic backend URL or fallback to env variable
const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [smsMode, setSmsMode] = useState(false);
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    // Don't check auth if we just came from a failed auth attempt
    if (location.state?.fromAuth) {
      setChecking(false);
      return;
    }

    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('session_token');
        if (token) {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data) {
            navigate("/jobs", { replace: true });
            return;
          }
        }
      } catch (error) {
        // Not authenticated
        localStorage.removeItem('session_token');
        localStorage.removeItem('user');
      }
      setChecking(false);
    };

    checkAuth();
  }, [navigate, location.state]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/jobs';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSendMagicLink = async () => {
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length < 10) {
      toast.error("Please enter a valid phone number.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/auth/magic-link/request`, { phone: cleaned });
      // Backend always returns success (no enumeration). We show a
      // friendly confirmation regardless.
      setSent(true);
    } catch (err) {
      // Only truly-broken cases (500s / offline) surface here.
      toast.error("Something went wrong — please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4">
      {checking ? (
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-zinc-200 border-t-[#207024]"></div>
      ) : (
        <div className="max-w-md w-full">
          <div className="text-center mb-12">
            <img
              src="https://customer-assets.emergentagent.com/job_window-screen-mgmt/artifacts/xq82sr3z_ScreenzZ-1024x1024.png"
              alt="ScreenzZ Logo"
              className="h-32 mx-auto mb-6"
            />
            <h1 className="font-barlow text-4xl font-bold text-zinc-950 uppercase tracking-tight mb-2">
              ScreenzZ
            </h1>
            <p className="font-inter text-lg text-zinc-600 font-medium">
              Job Management for Screen Techs
            </p>
          </div>

          <div className="bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-8 space-y-4">
            {!smsMode ? (
              <>
                <button
                  onClick={handleLogin}
                  data-testid="login-button"
                  className="w-full h-14 rounded-lg bg-[#207024] hover:bg-[#1a5c1d] text-white font-bold text-lg shadow-md active:scale-95 transition-transform"
                >
                  Sign in with Google
                </button>

                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-zinc-200"></div>
                  <span className="text-xs text-zinc-400 font-medium uppercase tracking-wide">or</span>
                  <div className="flex-1 h-px bg-zinc-200"></div>
                </div>

                <button
                  onClick={() => setSmsMode(true)}
                  data-testid="show-sms-login-button"
                  className="w-full h-12 rounded-lg border-2 border-[#207024] text-[#207024] font-semibold hover:bg-[#207024] hover:text-white transition-colors"
                >
                  Text Me a Login Link
                </button>
              </>
            ) : sent ? (
              <div className="text-center space-y-4" data-testid="magic-link-sent-panel">
                <div className="text-3xl">📲</div>
                <div className="font-inter font-semibold text-zinc-800">
                  Check your text messages.
                </div>
                <div className="text-sm text-zinc-600">
                  If your number is registered with ScreenzZ, we sent you a login link.
                  It expires in 10 minutes.
                </div>
                <button
                  onClick={() => { setSent(false); setSmsMode(false); setPhone(""); }}
                  data-testid="magic-link-back-button"
                  className="text-sm text-[#207024] font-semibold underline underline-offset-2"
                >
                  Back to login options
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <label className="block">
                  <span className="text-sm font-semibold text-zinc-700 mb-2 block">
                    Your phone number
                  </span>
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    autoFocus
                    placeholder="(555) 123-4567"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    data-testid="magic-link-phone-input"
                    className="w-full h-12 px-4 rounded-lg border-2 border-zinc-200 focus:border-[#207024] focus:outline-none text-lg"
                  />
                </label>
                <button
                  onClick={handleSendMagicLink}
                  disabled={sending || phone.replace(/\D/g, "").length < 10}
                  data-testid="magic-link-send-button"
                  className="w-full h-12 rounded-lg bg-[#207024] hover:bg-[#1a5c1d] text-white font-bold shadow-md disabled:opacity-50 active:scale-95 transition-transform"
                >
                  {sending ? "Sending…" : "Send Login Link"}
                </button>
                <button
                  onClick={() => setSmsMode(false)}
                  data-testid="magic-link-cancel-button"
                  className="w-full h-10 text-sm text-zinc-500 font-semibold hover:text-zinc-700"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          <p className="text-center text-sm text-zinc-500 mt-6 font-inter">
            Authorized users only
          </p>
        </div>
      )}
    </div>
  );
}
